/**
 * A per-product list-of-tiers RTL pricing section — `loanAmountUpticks` and
 * `maxLeverageAdjustments` both store `{ [product]: [row, …] }`.
 *
 * Products come from the base-rate map, so a product with no tiers still gets a
 * (empty) tab — you can't add a tier for a product that has no base rate, which
 * is the correct constraint: `compute_rtl_rate` returns an error for those.
 *
 * The grid is AG Grid with inline editing; `active` is a checkbox because an
 * inactive tier is kept for history rather than deleted.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import AddRounded from "@mui/icons-material/AddRounded";

import Flx from "../../../components/layout/Flx";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import PillSwitcher from "../shared/PillSwitcher";
import { removeColumn } from "../shared/adminTableActions";
import {
  PRICING_GRID_COL_DEF,
  PricingField,
  PricingGrid,
  PricingSection,
  SectionEmpty,
} from "./pricingUi";

const isBlank = (v) => v == null || String(v).trim() === "";

/** What an empty cell prompts with, by column type. */
const PLACEHOLDER = {
  dollar: "Enter an amount…",
  number: "Enter a value…",
  fraction: "Enter an LTV…",
  percent: "Enter a percent…",
  text: "Enter a label…",
};

const numericParser = (p) => {
  const v = String(p.newValue ?? "").trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : p.oldValue;
};

export default function TierListSection({
  id,
  title,
  helper,
  products,
  data,
  columns,
  newRow,
  version,
  lastEdit,
  onHistory,
  busy,
  onSave,
}) {
  const [draft, setDraft] = useState({});
  const [product, setProduct] = useState("");
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);

  // Shared by the initial seed and the reset button — both go back to the saved
  // `data` prop, which is the only source of truth for "unchanged".
  const reseed = useCallback(() => {
    setDraft(structuredClone(data || {}));
    setDirty(false);
    setNote("");
  }, [data]);

  useEffect(reseed, [reseed]);

  // Every base-rate product gets a tab, even when it has no tiers stored.
  const tabs = useMemo(
    () =>
      Array.from(new Set([...(products || []), ...Object.keys(data || {})])).map(
        (p) => ({ key: p, label: `${p} (${(data?.[p] || []).length})` }),
      ),
    [products, data],
  );

  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.key === product)) {
      setProduct(tabs[0].key);
    }
  }, [tabs, product]);

  const rows = useMemo(
    () => (draft[product] || []).map((r, i) => ({ ...r, __i: i })),
    [draft, product],
  );

  const columnDefs = useMemo(
    () =>
      [
        ...columns.map((c) => ({
          headerName: c.header,
          field: c.field,
          width: c.type === "text" ? 220 : 150,
          flex: c.type === "text" ? 1 : undefined,
          editable: true,
          // Everything but the label and the checkbox is a figure — right-align
          // it and set it in the tabular mono so columns read down. A blank in a
          // just-added row prompts instead of showing a dash.
          ...(["dollar", "number", "fraction", "percent"].includes(c.type)
            ? {
                cellClass: (p) => (isBlank(p.value) ? "num ph" : "num"),
                headerClass: "num",
              }
            : {}),
          ...(c.type === "text"
            ? {
                cellClass: (p) => (isBlank(p.value) ? "ph" : undefined),
                valueFormatter: (p) =>
                  isBlank(p.value) ? PLACEHOLDER.text : p.value,
              }
            : {}),
          ...(c.type === "bool"
            ? {
                cellRenderer: "agCheckboxCellRenderer",
                cellEditor: "agCheckboxCellEditor",
                width: 100,
              }
            : {}),
          ...(c.type === "dollar"
            ? {
                valueParser: numericParser,
                valueFormatter: (p) =>
                  isBlank(p.value)
                    ? PLACEHOLDER.dollar
                    : formatDollar(p.value, "—"),
              }
            : {}),
          ...(c.type === "number"
            ? {
                valueParser: numericParser,
                valueFormatter: (p) =>
                  isBlank(p.value) ? PLACEHOLDER.number : String(p.value),
              }
            : {}),
          // Percent stored verbatim (bps retired from RTL pricing 2026-08-19) —
          // no scaling in or out; what the user types is what is stored.
          ...(c.type === "percent"
            ? {
                valueParser: (p) => {
                  const raw = String(p.newValue ?? "").trim();
                  if (raw === "") return null;
                  const n = Number(raw);
                  return Number.isFinite(n)
                    ? Number(n.toFixed(4))
                    : p.oldValue;
                },
                // THREE decimals, not two: the desk quotes to the basis point,
                // and 0.125 rendered as "0.13%" is the exact failure the units
                // contract is about — a stored value the table displays as a
                // different one (rtl-pricing-units-contract.md, Display).
                valueFormatter: (p) =>
                  isBlank(p.value)
                    ? PLACEHOLDER.percent
                    : `${Number(p.value).toFixed(3)}%`,
              }
            : {}),
          ...(c.type === "fraction"
            ? {
                valueParser: numericParser,
                // Stored as a fraction; shown as a percent so 0.80 reads 80.0%.
                valueFormatter: (p) =>
                  isBlank(p.value)
                    ? PLACEHOLDER.fraction
                    : `${(Number(p.value) * 100).toFixed(1)}%`,
              }
            : {}),
        })),
        removeColumn((p) => {
          setDraft((prev) => {
            const next = structuredClone(prev);
            next[product] = (next[product] || []).filter(
              (_, j) => j !== p.data.__i,
            );
            return next;
          });
          setDirty(true);
        }),
      ],
    [columns, product],
  );

  const addRow = useCallback(() => {
    setDraft((prev) => {
      const next = structuredClone(prev);
      next[product] = [...(next[product] || []), newRow()];
      return next;
    });
    setDirty(true);
  }, [product, newRow]);

  const save = useCallback(() => {
    // Strip the grid-only index before persisting.
    const clean = Object.fromEntries(
      Object.entries(draft).map(([p, list]) => [
        p,
        (list || []).map(({ __i, ...rest }) => rest),
      ]),
    );
    onSave?.(clean, note || `${title} updated`)
      .then(() => setDirty(false))
      .catch(() => {});
  }, [draft, onSave, note, title]);

  return (
    <PricingSection
      id={id}
      title={title}
      desc={helper}
      version={version}
      lastEdit={lastEdit}
      onHistory={onHistory}
      stackActions
      dense
      actions={
        tabs.length ? (
          <PillSwitcher items={tabs} value={product} onChange={setProduct} />
        ) : null
      }
    >
      {!tabs.length ? (
        <SectionEmpty>
          No products configured — set base rates first; every other section is
          keyed by them.
        </SectionEmpty>
      ) : (
        <>
          {!rows.length ? (
            <SectionEmpty>No tiers for this product yet.</SectionEmpty>
          ) : (
            <PricingGrid rowCount={rows.length} maxRows={10}>
              <AgGridReact
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={{ ...PRICING_GRID_COL_DEF, sortable: false }}
                getRowId={(p) => `${product}-${p.data.__i}`}
                onCellValueChanged={(e) => {
                  setDraft((prev) => {
                    const next = structuredClone(prev);
                    next[product][e.data.__i][e.colDef.field] = e.newValue;
                    return next;
                  });
                  setDirty(true);
                }}
                stopEditingWhenCellsLoseFocus
              />
            </PricingGrid>
          )}

          {/* Add sits under the table it appends to. The commit/save controls
              only exist once there is something to commit. */}
          {/* Aligned on the controls, not their labels — the commit field is two
              elements tall now, the buttons one. */}
          <Flx
            g={1.5}
            sx={{ px: 2.5, pt: 0, pb: 2, alignItems: "flex-end" }}
            wrap
          >
            <Button
              size="small"
              variant="text"
              color="success"
              startIcon={<AddRounded />}
              onClick={addRow}
              disabled={!product}
            >
              Add tier
            </Button>

            {dirty ? (
              <>
                <PricingField
                  label="Commit message"
                  sx={{ minWidth: 240, flex: 1 }}
                >
                  <TextField
                    size="small"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </PricingField>
                <Button variant="text" onClick={reseed} disabled={busy}>
                  Reset changes
                </Button>
                <Button variant="contained" onClick={save} disabled={busy}>
                  Save section
                </Button>
              </>
            ) : null}
          </Flx>
        </>
      )}
    </PricingSection>
  );
}

TierListSection.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  helper: PropTypes.string,
  version: PropTypes.node,
  lastEdit: PropTypes.object,
  onHistory: PropTypes.func,
  products: PropTypes.array,
  data: PropTypes.object,
  columns: PropTypes.array.isRequired,
  newRow: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};
